(() => {
  if (customElements.get('wolf-purchasing')) return;
  class WolfPurchasingElement extends HTMLElement {
    async connectedCallback() {
      this.section = this.closest('[data-wolf-product-id]');
      this.cardPurchasing = this.hasAttribute('data-card-purchasing');
      await customElements.whenDefined('product-form');
      // Empty galleries are rendered without media-gallery.js on products without media.
      if (this.section.querySelector('media-gallery [data-media-id]')) await customElements.whenDefined('media-gallery');
      if (!this.isConnected) return;
      this.formElement = this.section.querySelector('product-form[data-wolf-unified="true"]');
      this.quantity = this.section.querySelector('input[name="quantity"]');
      this.picker = this.section.querySelector('variant-radios, variant-selects');
      this.events = new AbortController();
      try {
        this.config = JSON.parse(this.querySelector('[data-wolf-purchasing-data]').textContent);
        if (!window.WolfPurchasing || !this.formElement || !this.quantity) throw new Error('Missing purchasing configuration');
        this.cart = this.config.cart;
        // Do not cap the input at RTS rules: a larger whole addition may be valid as MTO.
        this.quantity.min = '1';
        this.quantity.step = '1';
        this.quantity.removeAttribute('max');
        this.section.addEventListener('change', (event) => {
          if (event.target === this.quantity || event.target.closest('variant-radios, variant-selects')) this.update();
        }, { signal: this.events.signal });
        this.quantity.addEventListener('input', () => this.update(), { signal: this.events.signal });
        if (this.cardPurchasing) {
          this.section.addEventListener('wolf:card-variant-change', (event) => {
            this.dataset.selectedVariant = String(event.detail.variantId);
            this.formElement.handleErrorMessage();
            this.update();
          }, { signal: this.events.signal });
          ['pointerenter', 'focusin'].forEach((type) => {
            this.section.addEventListener(type, () => {
              this.syncCardLiveRegion();
              return this.refreshCardCart();
            }, { signal: this.events.signal });
          });
          this.section.addEventListener('focusout', (event) => {
            this.syncCardLiveRegion(Boolean(event.relatedTarget && this.section.contains(event.relatedTarget)));
          }, { signal: this.events.signal });
        }
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        window.addEventListener('pageshow', (event) => {
          if (!this.cardPurchasing) this.refreshCart();
          else if (event.persisted) this.cardCartChecked = false;
        }, { signal: this.events.signal });
        this.update();
        if (!this.cardPurchasing) this.refreshCart();
      } catch (error) {
        console.error('[Wolf purchasing] Initialization failed', error);
        this.fail('Availability could not be checked. Please reload the page.');
      }
    }
    disconnectedCallback() { this.events?.abort(); this.unsubscribe?.(); this.stopAvailabilityMotion(); }
    syncCardLiveRegion(focused = this.section.matches?.(':focus-within')) {
      if (!this.cardPurchasing) return;
      const status = this.querySelector('.wolf-availability');
      const live = focused ? 'polite' : 'off';
      if (status && status.getAttribute('aria-live') !== live) status.setAttribute('aria-live', live);
    }
    updateAvailabilityText(node, value) {
      if (node && (!this.cardPurchasing || node.textContent !== value)) node.textContent = value;
    }
    refreshCardCart() {
      if (this.cardCartChecked) return;
      this.cardCartChecked = true;
      return this.refreshCart();
    }
    onCartUpdate(event) {
      if (!this.cardPurchasing) return this.refreshCart();
      // A collection can contain dozens of forms. Reuse confirmed cart snapshots
      // instead of issuing a separate cart request from every card after an add.
      if (Array.isArray(event?.cartData?.items)) {
        this.cartRevision = (this.cartRevision || 0) + 1;
        this.cart = event.cartData;
        this.cardCartChecked = true;
        if (!WolfPurchasing.hydrateCart(this.cart, this.config.cart)) return this.refreshCart();
        this.update();
      } else {
        this.cardCartChecked = false;
        if (this.section.matches?.(':hover, :focus-within')) return this.refreshCardCart();
      }
    }
    stopAvailabilityMotion() {
      this.availabilityMotion?.forEach((animation) => animation.cancel());
      this.availabilityMotion = [];
      this.availabilityGhost?.remove();
      this.availabilityGhost = null;
    }
    prepareAvailabilityMotion(status, state) {
      if (this.cardPurchasing || status.dataset.state === state) return null;
      const previousHeight = this.getBoundingClientRect?.().height;
      const previousBounds = status.getBoundingClientRect?.();
      this.stopAvailabilityMotion();
      if (!status.animate || !status.dataset.state || status.dataset.state === 'loading' ||
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return null;
      const ghost = status.cloneNode(true);
      ghost.classList.add('wolf-availability--outgoing');
      ghost.setAttribute('aria-hidden', 'true');
      ['role', 'aria-live', 'aria-atomic'].forEach((attribute) => ghost.removeAttribute(attribute));
      ghost.style.width = `${previousBounds.width}px`;
      ghost.style.height = `${previousBounds.height}px`;
      return { ghost, previousHeight };
    }
    animateAvailability(status, transition) {
      if (!transition) return;
      const { ghost, previousHeight } = transition;
      const height = this.getBoundingClientRect().height;
      const timing = { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
      this.append(ghost);
      this.availabilityGhost = ghost;
      const outgoing = ghost.animate([
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-2px)' },
      ], { duration: 150, easing: 'ease-out', fill: 'forwards' });
      const incoming = status.animate([
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ], timing);
      this.availabilityMotion = [outgoing, incoming];
      if (Math.abs(previousHeight - height) > 0.5) {
        this.availabilityMotion.push(this.animate([{ height: `${previousHeight}px` }, { height: `${height}px` }], timing));
      }
      outgoing.finished.then(() => ghost.remove(), () => ghost.remove());
    }
    selected() {
      if (this.cardPurchasing) {
        const variant = this.config.variants.find((candidate) => String(candidate.id) === this.dataset.selectedVariant);
        return variant ? WolfPurchasing.physicalOptions(variant) : { __missing_card_variant: true };
      }
      const options = {};
      this.picker?.querySelectorAll('fieldset[data-wolf-option-name]').forEach((fieldset) => {
        const control = fieldset.querySelector('input:checked');
        if (control) options[fieldset.dataset.wolfOptionName] = control.value;
      });
      this.picker?.querySelectorAll('select[data-wolf-option-name]').forEach((control) => { options[control.dataset.wolfOptionName] = control.value; });
      if (!Object.keys(options).length) {
        const variant = this.config.variants.find((candidate) => !candidate.hidden && String(candidate.productId) === String(this.config.productId));
        return variant ? WolfPurchasing.physicalOptions(variant) : {};
      }
      return options;
    }
    update() {
      if (!this.config) return;
      const result = WolfPurchasing.resolve(this.config, this.selected(), Number(this.quantity.value), this.cart);
      this.result = result;
      this.syncCardLiveRegion();
      const status = this.querySelector('.wolf-availability');
      const transition = this.prepareAvailabilityMotion(status, result.state);
      status.dataset.state = result.state;
      this.updateAvailabilityText(this.querySelector('[data-wolf-availability]'), WolfPurchasing.message(result));
      this.updateAvailabilityText(this.querySelector('[data-wolf-lead-time]'), result.purchasable && result.lead ? (result.state === 'mto' && result.leadWeeks ? result.lead : `Ships in ${result.lead}`) : '');
      const note = this.querySelector('[data-wolf-cart-note]');
      note.hidden = !result.inCart;
      this.updateAvailabilityText(note, result.inCart ? 'Accounts for pieces already in your bag.' : '');
      const button = this.formElement.submitButton;
      button.disabled = !result.purchasable || Boolean(this.pending);
      button.querySelector('span').textContent = this.cardPurchasing
        ? (result.purchasable || this.pending ? 'Add to bag' : 'Unavailable')
        : (result.purchasable || this.pending ? 'Add to bag' : 'UNAVAILABLE');
      const id = this.formElement.form.querySelector('[name="id"]');
      id.disabled = !result.purchasable;
      id.value = result.purchasable ? result.variant.id : '';
      if (result.variant || result.canonicalVariant) this.syncVariant({ ...result, variant: result.variant || result.canonicalVariant });
      this.animateAvailability(status, transition);
    }
    syncVariant(result) {
      const variant = result.variant;
      const canonical = result.canonicalVariant;
      const fingerprint = JSON.stringify([variant.id, variant.price, variant.compareAtPrice, variant.unitPriceText, variant.unitPriceMeasurement, variant.sku]);
      if (this.lastVariant === fingerprint) return;
      this.lastVariant = fingerprint;
      const price = this.section.querySelector(this.cardPurchasing ? '.card-information' : '.price-box');
      if (price) {
        price.querySelectorAll('.price-item--regular').forEach((node) => { node.textContent = variant.compareAtPrice > variant.price ? variant.compareAtPriceText : variant.priceText; });
        price.querySelectorAll('.price-item--sale').forEach((node) => { node.textContent = variant.priceText; });
        price.querySelector('.price')?.classList.toggle('price--on-sale', variant.compareAtPrice > variant.price);
        const unit = price.querySelector('.unit-price');
        if (unit) unit.classList.toggle('hidden', !variant.unitPriceMeasurement);
        if (variant.unitPriceMeasurement) {
          const measurement = variant.unitPriceMeasurement;
          const unitValue = price.querySelector('.price-item--last > span:first-child');
          if (unitValue) unitValue.textContent = variant.unitPriceText;
          const unitReference = price.querySelector('.price-item--last > span:last-child');
          if (unitReference) unitReference.textContent = `${measurement.reference_value === 1 ? '' : measurement.reference_value}${measurement.reference_unit}`;
        }
      }
      if (this.cardPurchasing) {
        this.dispatchEvent(new CustomEvent('wolf:fulfillment-change', { bubbles: true, detail: { variantId: variant.id, fulfillment: result.state } }));
        return;
      }
      const sku = this.section.querySelector('[id^="Sku-"]');
      if (sku) {
        const label = sku.querySelector('.visually-hidden')?.cloneNode(true);
        sku.replaceChildren();
        if (label) sku.append(label);
        sku.append(document.createTextNode(variant.sku || ''));
        sku.classList.toggle('visibility-hidden', !variant.sku);
      }
      if (this.picker) {
        this.picker.currentVariant = canonical;
        // Linked products share physical media; never move a sibling product's media ID into this gallery.
        this.picker.updateMedia?.();
      }
      const installments = this.section.querySelector('.installments');
      const terms = this.section.querySelector(`template[data-wolf-terms-product="${variant.productId}"]`);
      if (installments && terms && installments.dataset.productId !== String(variant.productId)) {
        const content = terms.content.cloneNode(true);
        const selectedId = content.querySelector('[name="id"]');
        if (selectedId) selectedId.value = variant.id;
        // Native terms initialize asynchronously when the linked product changes.
        content.querySelector('shopify-payment-terms')?.setAttribute('variant-id', variant.id);
        if (typeof installments.replaceTerms === 'function') installments.replaceTerms(content);
        else installments.replaceChildren(content);
        installments.dataset.productId = String(variant.productId);
      }
      const installment = installments?.querySelector('[name="id"]');
      if (installment) { installment.value = variant.id; installment.dispatchEvent(new Event('change', { bubbles: true })); }
      this.picker?.updateShareUrl?.();
      const rules = this.section.querySelector('.quantity__rules');
      if (rules) {
        const rule = variant.quantityRule || {};
        rules.textContent = [rule.min > 1 ? `Minimum ${rule.min}` : '', rule.max ? `Maximum ${rule.max}` : '', rule.increment > 1 ? `Multiples of ${rule.increment}` : ''].filter(Boolean).join(' · ');
      }
      if (!this.closest('quick-add-modal')) {
        const url = new URL(location.href);
        url.searchParams.set('variant', canonical.id);
        history.replaceState({}, '', url);
      }
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.sectionId, variant: canonical, html: document } });
      this.dispatchEvent(new CustomEvent('wolf:fulfillment-change', { bubbles: true, detail: { variantId: variant.id, fulfillment: result.state } }));
    }
    async readCart() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to read bag');
      return response.json();
    }
    async refreshCart() {
      const revision = this.cartRevision = (this.cartRevision || 0) + 1;
      try {
        const cart = await this.readCart();
        if (revision !== this.cartRevision || !this.isConnected) return;
        if (!WolfPurchasing.hydrateCart(cart, this.config.cart)) {
          const config = await this.freshConfiguration();
          if (revision !== this.cartRevision || !this.isConnected) return;
          this.config = config;
        }
        this.cart = cart;
        this.update();
      } catch (_) {
        if (revision === this.cartRevision) {
          if (this.cardPurchasing) this.cardCartChecked = false;
          this.fail('Your bag could not be checked. Reload the page before adding this item.');
        }
      }
    }
    async freshConfiguration() {
      const url = new URL(this.config.url, location.origin);
      url.searchParams.set('section_id', 'wolf-purchasing-data');
      url.searchParams.set('_', Date.now());
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to check availability');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = html.querySelector('[data-wolf-purchasing-data]');
      if (!data) throw new Error('Missing availability');
      return JSON.parse(data.textContent);
    }
    fingerprint(result) { return [result.state, result.variant?.id, result.variant?.price, result.lead].join('|'); }
    fail(message) {
      this.stopAvailabilityMotion();
      this.syncCardLiveRegion();
      if (this.formElement?.submitButton) { this.formElement.submitButton.disabled = true; this.formElement.handleErrorMessage(message); }
      const label = this.querySelector('[data-wolf-availability]');
      this.updateAvailabilityText(label, message);
      this.querySelector('.wolf-availability')?.setAttribute('data-state', 'unavailable');
      const lead = this.querySelector('[data-wolf-lead-time]');
      this.updateAvailabilityText(lead, '');
    }
    async reconcile(form, variantId, notice = '') {
      this.cart = await this.readCart();
      let rendered;
      if (form.cart) {
        const sections = form.cart.getSectionsToRender().map((section) => section.id);
        const url = new URL(window.routes.cart_url, location.origin);
        url.searchParams.set('sections', sections.join(','));
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to refresh bag');
        rendered = await response.json();
        if (sections.some((id) => !rendered[id])) throw new Error('Unable to refresh bag');
        form.cart.classList.toggle('is-empty', this.cart.item_count === 0);
      }
      // Publish before rendering so the previous drawer items cannot replace its new notice.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: this.cart });
      if (form.cart) {
        const state = { id: variantId, sections: rendered };
        if (form.renderCart) form.renderCart(state, notice);
        else form.cart.renderContents(state, notice);
      }
    }
    async submit(form) {
      if (this.pending || !this.result?.purchasable) return;
      const displayed = this.fingerprint(this.result);
      const selected = this.selected();
      const quantity = Number(this.quantity.value);
      const data = new FormData(form.form);
      data.set('quantity', quantity);
      this.pending = true;
      this.cartRevision = (this.cartRevision || 0) + 1;
      this.update();
      form.handleErrorMessage();
      form.submitButton.classList.add('loading');
      form.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      form.cart?.setActiveElement(document.activeElement);
      const run = window.WolfCartMutation?.run || ((task) => task());
      try {
        await run(async () => {
          const [config, cart] = await Promise.all([this.freshConfiguration(), this.readCart()]);
          this.config = config;
          this.cart = cart;
          const fresh = WolfPurchasing.resolve(config, selected, quantity, cart);
          const changedSelection = JSON.stringify(this.selected()) !== JSON.stringify(selected) || Number(this.quantity.value) !== quantity;
          this.update();
          if (changedSelection || !fresh.purchasable || this.fingerprint(fresh) !== displayed) {
            form.handleErrorMessage(fresh.state === 'mto' ? 'Availability has changed. This quantity is now made to order. Review the shipping estimate, then select Add to bag again to confirm.' : 'Availability or pricing has changed. Please review your selection before adding it to your bag.');
            return;
          }
          data.set('id', fresh.variant.id);
          if (config.nativeFulfillment) {
            const properties = WolfPurchasing.purchaseProperties(fresh);
            data.delete('properties[_wolf_mto_weeks]');
            Object.entries(properties).forEach(([key, value]) => data.set(`properties[${key}]`, value));
          }
          if (form.cart) {
            data.set('sections', form.cart.getSectionsToRender().map((section) => section.id).join(','));
            data.set('sections_url', location.pathname);
          }
          let attempted = false;
          try {
            attempted = true;
            const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: data });
            const added = await response.json();
            if (!response.ok || added.status) {
              const notice = 'Availability changed while adding your item. Your bag has been refreshed; please review the quantities below.';
              await this.reconcile(form, fresh.variant.id, notice);
              if (typeof form.cart?.showNotice !== 'function') form.handleErrorMessage(notice);
              publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: fresh.variant.id, errors: added.errors || added.description, message: added.message });
              return;
            }
            await this.reconcile(form, fresh.variant.id);
            if (!form.cart) location.assign(window.routes.cart_url);
          } catch (error) {
            // A lost response is not evidence that the addition failed. Never retry as MTO.
            if (attempted) {
              const notice = 'We could not confirm the change. Check your bag before adding this item again.';
              let refreshed = false;
              try {
                await this.reconcile(form, fresh.variant.id, notice);
                refreshed = typeof form.cart?.showNotice === 'function';
              } catch (_) { /* Keep the actionable uncertainty below. */ }
              if (!refreshed) form.handleErrorMessage(notice);
            } else throw error;
          }
        });
      } catch (_) {
        form.handleErrorMessage('Availability could not be checked. Please try again; nothing was submitted from this attempt.');
      } finally {
        this.pending = false;
        form.submitButton.classList.remove('loading');
        form.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        this.update();
      }
    }
  }
  customElements.define('wolf-purchasing', WolfPurchasingElement);
})();
